import { useState, useRef, useEffect } from 'react';

interface ApplicationFormProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function ApplicationForm({ isOpen, onClose }: ApplicationFormProps) {
  const [step, setStep] = useState(1);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState('');
  const overlayRef = useRef<HTMLDivElement>(null);

  const [formData, setFormData] = useState({
    nome: '',
    email: '',
    telefone: '',
    idade: '',
    data_casamento: '',
    cidade: '',
    num_convidados: '',
    como_conheceu: '',
    objetivo: '',
    ingresso_interesse: '',
  });

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    if (isOpen) window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [isOpen, onClose]);

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === overlayRef.current) onClose();
  };

  const updateField = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const validateStep1 = () => {
    if (!formData.nome.trim()) return 'Por favor, informe seu nome completo.';
    if (!formData.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) return 'Por favor, informe um email válido.';
    if (!formData.telefone.trim()) return 'Por favor, informe seu telefone/WhatsApp.';
    if (!formData.idade.trim()) return 'Por favor, informe sua idade.';
    if (!formData.cidade.trim()) return 'Por favor, informe sua cidade.';
    return '';
  };

  const validateStep2 = () => {
    if (!formData.data_casamento.trim()) return 'Por favor, informe a data prevista do casamento.';
    if (!formData.num_convidados.trim()) return 'Por favor, informe o número estimado de convidados.';
    if (!formData.como_conheceu.trim()) return 'Por favor, informe como conheceu o workshop.';
    if (!formData.ingresso_interesse.trim()) return 'Por favor, selecione o ingresso de interesse.';
    if (!formData.objetivo.trim()) return 'Por favor, compartilhe brevemente por que você merece uma vaga.';
    return '';
  };

  const handleNext = () => {
    const error = validateStep1();
    if (error) {
      setFormError(error);
      return;
    }
    setFormError('');
    setStep(2);
  };

  const handleBack = () => {
    setFormError('');
    setStep(1);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const error = validateStep2();
    if (error) {
      setFormError(error);
      return;
    }
    setFormError('');
    setSubmitting(true);

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      await fetch('https://readdy.ai/api/form/d8o0grd0ihgem5t5pl50', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formBody.toString(),
      });

      setSubmitted(true);
    } catch {
      setFormError('Ocorreu um erro ao enviar. Por favor, tente novamente.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      ref={overlayRef}
      onClick={handleOverlayClick}
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      style={{ backgroundColor: 'oklch(var(--foreground-950) / 0.6)', backdropFilter: 'blur(4px)' }}
    >
      <div
        className="relative w-full max-w-xl rounded-lg overflow-hidden animate-fade-in-up"
        style={{ backgroundColor: 'oklch(var(--background-50))' }}
      >
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 w-8 h-8 rounded-full flex items-center justify-center z-10 cursor-pointer transition-colors duration-300"
          style={{ backgroundColor: 'oklch(var(--background-100))', color: 'oklch(var(--foreground-500))' }}
        >
          <i className="ri-close-line"></i>
        </button>

        {submitted ? (
          /* Success State */
          <div className="p-10 md:p-14 text-center">
            <div className="w-16 h-16 mx-auto mb-6 rounded-full flex items-center justify-center" style={{ backgroundColor: 'oklch(var(--primary-100))' }}>
              <i className="ri-check-line text-2xl" style={{ color: 'oklch(var(--primary-500))' }}></i>
            </div>
            <h3 className="font-heading text-2xl md:text-3xl mb-4" style={{ color: 'oklch(var(--foreground-900))' }}>
              Aplicação Enviada!
            </h3>
            <p className="text-sm leading-relaxed mb-6" style={{ color: 'oklch(var(--foreground-500))' }}>
              Recebemos sua aplicação com sucesso. Nossa equipe de curadoria avaliará seu perfil
              e entraremos em contato em breve pelos canais informados.
            </p>
            <button
              onClick={onClose}
              className="px-8 py-3 text-xs tracking-[0.15em] uppercase rounded-md whitespace-nowrap cursor-pointer font-semibold transition-all duration-300 hover:opacity-90"
              style={{ backgroundColor: '#ADC178', color: '#FFFFFF' }}
            >
              Fechar
            </button>
          </div>
        ) : (
          /* Form */
          <form data-readdy-form="" onSubmit={handleSubmit}>
            {/* Header */}
            <div className="px-8 md:px-10 pt-10 pb-6 text-center">
              <p className="text-[10px] tracking-[0.3em] uppercase mb-3" style={{ color: 'oklch(var(--accent-500))' }}>
                Aplicação para Vaga
              </p>
              <h3 className="font-heading text-xl md:text-2xl mb-2" style={{ color: 'oklch(var(--foreground-900))' }}>
                {step === 1 ? 'Seus Dados' : 'Sobre o Casamento'}
              </h3>
              <p className="text-xs" style={{ color: 'oklch(var(--foreground-400))' }}>
                Etapa {step} de 2
              </p>
              {/* Progress bar */}
              <div className="mt-4 h-1 rounded-full overflow-hidden" style={{ backgroundColor: 'oklch(var(--background-200))' }}>
                <div
                  className="h-full transition-all duration-500 rounded-full"
                  style={{
                    width: step === 1 ? '50%' : '100%',
                    backgroundColor: 'oklch(var(--primary-500))',
                  }}
                ></div>
              </div>
            </div>

            {/* Error */}
            {formError && (
              <div className="mx-8 md:mx-10 mb-4 px-4 py-3 rounded-md text-xs" style={{ backgroundColor: 'oklch(var(--accent-100))', color: 'oklch(var(--accent-800))' }}>
                {formError}
              </div>
            )}

            {/* Step 1 */}
            {step === 1 && (
              <div className="px-8 md:px-10 pb-8 space-y-4">
                <div>
                  <label className="block text-xs tracking-wide mb-1.5 font-medium" style={{ color: 'oklch(var(--foreground-700))' }}>
                    Nome Completo *
                  </label>
                  <input
                    type="text"
                    name="nome"
                    value={formData.nome}
                    onChange={(e) => updateField('nome', e.target.value)}
                    className="w-full px-4 py-3 text-sm rounded-md outline-none transition-all duration-300 border"
                    style={{
                      backgroundColor: 'oklch(var(--background-100))',
                      borderColor: 'oklch(var(--background-200))',
                      color: 'oklch(var(--foreground-800))',
                    }}
                    placeholder="Seu nome completo"
                  />
                </div>
                <div>
                  <label className="block text-xs tracking-wide mb-1.5 font-medium" style={{ color: 'oklch(var(--foreground-700))' }}>
                    Email *
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={(e) => updateField('email', e.target.value)}
                    className="w-full px-4 py-3 text-sm rounded-md outline-none transition-all duration-300 border"
                    style={{
                      backgroundColor: 'oklch(var(--background-100))',
                      borderColor: 'oklch(var(--background-200))',
                      color: 'oklch(var(--foreground-800))',
                    }}
                    placeholder="seu@email.com"
                  />
                </div>
                <div>
                  <label className="block text-xs tracking-wide mb-1.5 font-medium" style={{ color: 'oklch(var(--foreground-700))' }}>
                    Telefone / WhatsApp *
                  </label>
                  <input
                    type="tel"
                    name="telefone"
                    value={formData.telefone}
                    onChange={(e) => updateField('telefone', e.target.value)}
                    className="w-full px-4 py-3 text-sm rounded-md outline-none transition-all duration-300 border"
                    style={{
                      backgroundColor: 'oklch(var(--background-100))',
                      borderColor: 'oklch(var(--background-200))',
                      color: 'oklch(var(--foreground-800))',
                    }}
                    placeholder="(16) 99999-0000"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs tracking-wide mb-1.5 font-medium" style={{ color: 'oklch(var(--foreground-700))' }}>
                      Idade *
                    </label>
                    <input
                      type="number"
                      name="idade"
                      value={formData.idade}
                      onChange={(e) => updateField('idade', e.target.value)}
                      className="w-full px-4 py-3 text-sm rounded-md outline-none transition-all duration-300 border"
                      style={{
                        backgroundColor: 'oklch(var(--background-100))',
                        borderColor: 'oklch(var(--background-200))',
                        color: 'oklch(var(--foreground-800))',
                      }}
                      placeholder="Sua idade"
                    />
                  </div>
                  <div>
                    <label className="block text-xs tracking-wide mb-1.5 font-medium" style={{ color: 'oklch(var(--foreground-700))' }}>
                      Cidade *
                    </label>
                    <input
                      type="text"
                      name="cidade"
                      value={formData.cidade}
                      onChange={(e) => updateField('cidade', e.target.value)}
                      className="w-full px-4 py-3 text-sm rounded-md outline-none transition-all duration-300 border"
                      style={{
                        backgroundColor: 'oklch(var(--background-100))',
                        borderColor: 'oklch(var(--background-200))',
                        color: 'oklch(var(--foreground-800))',
                      }}
                      placeholder="Sua cidade"
                    />
                  </div>
                </div>
                <div className="pt-4">
                  <button
                    type="button"
                    onClick={handleNext}
                    className="w-full py-3.5 text-xs tracking-[0.2em] uppercase rounded-md whitespace-nowrap cursor-pointer font-semibold transition-all duration-300 hover:opacity-90"
                    style={{ backgroundColor: '#ADC178', color: '#FFFFFF' }}
                  >
                    Próximo Passo
                  </button>
                </div>
              </div>
            )}

            {/* Step 2 */}
            {step === 2 && (
              <div className="px-8 md:px-10 pb-8 space-y-4">
                <div>
                  <label className="block text-xs tracking-wide mb-1.5 font-medium" style={{ color: 'oklch(var(--foreground-700))' }}>
                    Data Prevista do Casamento *
                  </label>
                  <input
                    type="date"
                    name="data_casamento"
                    value={formData.data_casamento}
                    onChange={(e) => updateField('data_casamento', e.target.value)}
                    className="w-full px-4 py-3 text-sm rounded-md outline-none transition-all duration-300 border"
                    style={{
                      backgroundColor: 'oklch(var(--background-100))',
                      borderColor: 'oklch(var(--background-200))',
                      color: 'oklch(var(--foreground-800))',
                    }}
                  />
                </div>
                <div>
                  <label className="block text-xs tracking-wide mb-1.5 font-medium" style={{ color: 'oklch(var(--foreground-700))' }}>
                    Número Estimado de Convidados *
                  </label>
                  <select
                    name="num_convidados"
                    value={formData.num_convidados}
                    onChange={(e) => updateField('num_convidados', e.target.value)}
                    className="w-full px-4 py-3 text-sm rounded-md outline-none transition-all duration-300 border cursor-pointer"
                    style={{
                      backgroundColor: 'oklch(var(--background-100))',
                      borderColor: 'oklch(var(--background-200))',
                      color: 'oklch(var(--foreground-800))',
                    }}
                  >
                    <option value="">Selecione...</option>
                    <option value="50-100">50 a 100 convidados</option>
                    <option value="100-200">100 a 200 convidados</option>
                    <option value="200-300">200 a 300 convidados</option>
                    <option value="300+">Mais de 300 convidados</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs tracking-wide mb-1.5 font-medium" style={{ color: 'oklch(var(--foreground-700))' }}>
                    Como conheceu o Workshop? *
                  </label>
                  <select
                    name="como_conheceu"
                    value={formData.como_conheceu}
                    onChange={(e) => updateField('como_conheceu', e.target.value)}
                    className="w-full px-4 py-3 text-sm rounded-md outline-none transition-all duration-300 border cursor-pointer"
                    style={{
                      backgroundColor: 'oklch(var(--background-100))',
                      borderColor: 'oklch(var(--background-200))',
                      color: 'oklch(var(--foreground-800))',
                    }}
                  >
                    <option value="">Selecione...</option>
                    <option value="instagram">Instagram</option>
                    <option value="indicacao">Indicação de amiga(o)</option>
                    <option value="fornecedor">Indicação de fornecedor</option>
                    <option value="google">Google / Busca online</option>
                    <option value="influencer">Através da influenciadora</option>
                    <option value="outro">Outro</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs tracking-wide mb-1.5 font-medium" style={{ color: 'oklch(var(--foreground-700))' }}>
                    Ingresso de Interesse *
                  </label>
                  <select
                    name="ingresso_interesse"
                    value={formData.ingresso_interesse}
                    onChange={(e) => updateField('ingresso_interesse', e.target.value)}
                    className="w-full px-4 py-3 text-sm rounded-md outline-none transition-all duration-300 border cursor-pointer"
                    style={{
                      backgroundColor: 'oklch(var(--background-100))',
                      borderColor: 'oklch(var(--background-200))',
                      color: 'oklch(var(--foreground-800))',
                    }}
                  >
                    <option value="">Selecione...</option>
                    <option value="future_pass">Future Pass</option>
                    <option value="future_experience">Future Experience</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs tracking-wide mb-1.5 font-medium" style={{ color: 'oklch(var(--foreground-700))' }}>
                    Por que você merece uma vaga? *
                  </label>
                  <textarea
                    name="objetivo"
                    value={formData.objetivo}
                    onChange={(e) => updateField('objetivo', e.target.value)}
                    maxLength={500}
                    rows={3}
                    className="w-full px-4 py-3 text-sm rounded-md outline-none transition-all duration-300 border resize-none"
                    style={{
                      backgroundColor: 'oklch(var(--background-100))',
                      borderColor: 'oklch(var(--background-200))',
                      color: 'oklch(var(--foreground-800))',
                    }}
                    placeholder="Conte-nos brevemente sobre você e sua visão para o grande dia..."
                  ></textarea>
                  <p className="text-[10px] mt-1 text-right" style={{ color: 'oklch(var(--foreground-300))' }}>
                    {formData.objetivo.length}/500
                  </p>
                </div>
                <div className="flex gap-3 pt-2">
                  <button
                    type="button"
                    onClick={handleBack}
                    className="flex-1 py-3.5 text-xs tracking-[0.2em] uppercase rounded-md whitespace-nowrap cursor-pointer font-semibold transition-all duration-300 border"
                    style={{
                      backgroundColor: 'transparent',
                      borderColor: 'oklch(var(--background-200))',
                      color: 'oklch(var(--foreground-600))',
                    }}
                  >
                    Voltar
                  </button>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="flex-1 py-3.5 text-xs tracking-[0.2em] uppercase rounded-md whitespace-nowrap cursor-pointer font-semibold transition-all duration-300 hover:opacity-90"
                    style={{
                      backgroundColor: '#ADC178',
                      color: '#FFFFFF',
                      opacity: submitting ? 0.7 : 1,
                    }}
                  >
                    {submitting ? 'Enviando...' : 'Enviar Aplicação'}
                  </button>
                </div>
              </div>
            )}
          </form>
        )}
      </div>
    </div>
  );
}